import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { relationshipModel } from '~/models';
import { COBStep, getInitialSelectedBundleId } from '../../ClientOnboarding.utils';
import { useCobStorage } from '../../CobStorageContext';
const PACKAGE_SELECTION_DELAY_MS = 1000;
export const PackageSelectionContext = createContext(undefined);
export function usePackageSelection() {
    const context = useContext(PackageSelectionContext);
    if (!context) {
        throw new Error('usePackageSelection must be used within a PackageSelectionProvider');
    }
    return context;
}
export const usePackageSelectionState = (param)=>{
    let { relationship, currentStep, userId, handleNext } = param;
    const { getFromStorage, setToStorage } = useCobStorage(relationship);
    const [selectedBundleId, setSelectedBundleId] = useState(()=>getInitialSelectedBundleId(getFromStorage, relationship, userId));
    const [isNavigating, setIsNavigating] = useState(false);
    const navigationTimerRef = useRef(null);
    useEffect(()=>{
        if (currentStep === COBStep.selectAPackage) {
            setIsNavigating(false);
        }
    }, [
        currentStep
    ]);
    useEffect(()=>{
        return ()=>{
            if (navigationTimerRef.current != null) {
                clearTimeout(navigationTimerRef.current);
            }
        };
    }, []);
    const updateSelectedPackage = useCallback((bundleId)=>{
        setSelectedBundleId(bundleId);
        setToStorage('selected_bundle_id', bundleId);
        const includedByDefault = relationshipModel.includedByDefaultOptionalServices(relationship, bundleId);
        setToStorage('selected_optional_services', includedByDefault);
    }, [
        relationship,
        setToStorage
    ]);
    const selectPackage = useCallback((bundleId)=>{
        if (navigationTimerRef.current != null) {
            clearTimeout(navigationTimerRef.current);
        }
        setIsNavigating(true);
        updateSelectedPackage(bundleId);
        navigationTimerRef.current = setTimeout(()=>{
            navigationTimerRef.current = null;
            handleNext();
        }, PACKAGE_SELECTION_DELAY_MS);
    }, [
        updateSelectedPackage,
        handleNext
    ]);
    const packageSelectionContextValue = useMemo(()=>({
            selectedBundleId,
            selectPackage,
            updateSelectedPackage,
            isNavigating
        }), [
        selectedBundleId,
        selectPackage,
        updateSelectedPackage,
        isNavigating
    ]);
    const isPrimaryContact = relationshipModel.isBuyer(relationship, userId);
    const isOnPackagesStep = currentStep === COBStep.selectAPackage;
    const hideNextButton = isOnPackagesStep && (!isPrimaryContact || !selectedBundleId || isNavigating);
    return {
        selectedBundleId,
        isNavigating,
        hideNextButton,
        packageSelectionContextValue
    };
};
